export default function AwardsRecognition() {
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-yellow-50 rounded-full blur-3xl opacity-60 translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-50 rounded-full blur-3xl opacity-60 -translate-x-1/3 translate-y-1/3"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-700 text-sm font-semibold uppercase tracking-wider rounded-full mb-4">
            <i className="ri-award-fill"></i>
            Awards & Recognition
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
            Excellence
            <span className="text-yellow-500"> Recognized</span>
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Our commitment to quality STEM education and industry-ready curriculum continues to earn recognition at the highest levels.
          </p>
        </div>

        {/* Main Award Card */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Award Photo */}
          <div className="relative">
            {/* Decorative frame */}
            <div className="absolute -inset-3 bg-gradient-to-br from-yellow-400 via-yellow-300 to-amber-500 rounded-3xl opacity-20 blur-sm"></div>
            <div className="relative rounded-2xl overflow-hidden border-4 border-yellow-100">
              <img
                src="https://static.readdy.ai/image/c2fbe695e557781efe45beda2c2e7ab3/d1832dc45c0884037c71fb31461d7f9a.jpeg"
                alt="Best Company of the Year 2025 Award — STEMForge Tech Labs"
                className="w-full h-[420px] object-cover object-top"
              />
              {/* Award Badge Overlay */}
              <div className="absolute top-5 left-5">
                <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white text-sm font-bold rounded-full">
                  <i className="ri-trophy-fill text-lg"></i>
                  Best Company of the Year — 2025
                </div>
              </div>
              {/* Awarding body */}
              <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-yellow-300 text-sm font-semibold tracking-wide">Awarded by</p>
                <p className="text-white text-lg font-bold">Aram Virudhugal, Chennai</p>
              </div>
            </div>
          </div>

          {/* Award Details */}
          <div>
            {/* Trophy Icon */}
            <div className="w-16 h-16 rounded-2xl bg-yellow-100 flex items-center justify-center mb-6">
              <i className="ri-trophy-fill text-3xl text-yellow-500"></i>
            </div>

            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight">
              Best Company of the Year
              <span className="block text-yellow-500">2025</span>
            </h3>

            <p className="text-gray-600 text-sm leading-relaxed mb-8">
              STEMForge Tech Labs is honored to receive the <strong>"Best Company of the Year – 2025"</strong> award by Aram Virudhugal, Chennai — a recognition of our dedication to delivering high-quality STEM education with an industry-ready curriculum tailored for schools and colleges.
            </p>

            {/* Key Highlights */}
            <div className="space-y-4 mb-8">
              {[
                {
                  icon: "ri-medal-2-fill",
                  color: "text-yellow-500",
                  bg: "bg-yellow-50",
                  title: "Best Company of the Year – 2025",
                  desc: "Awarded by Aram Virudhugal, Chennai"
                },
                {
                  icon: "ri-shield-check-fill",
                  color: "text-emerald-500",
                  bg: "bg-emerald-50",
                  title: "ISO Certified Organization",
                  desc: "Internationally recognized quality standards"
                },
                {
                  icon: "ri-star-fill",
                  color: "text-amber-500",
                  bg: "bg-amber-50",
                  title: "100% Satisfaction Rate",
                  desc: "Among students, schools, and colleges"
                },
                {
                  icon: "ri-lightbulb-flash-fill",
                  color: "text-teal-500",
                  bg: "bg-teal-50",
                  title: "Industry-Ready Curriculum",
                  desc: "Transforming classrooms into innovation hubs"
                }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className={`w-11 h-11 flex-shrink-0 rounded-xl ${item.bg} flex items-center justify-center`}>
                    <i className={`${item.icon} text-xl ${item.color}`}></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{item.title}</div>
                    <div className="text-gray-500 text-sm">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mission Quote Banner */}
        <div className="relative bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-10 overflow-hidden text-center">
          {/* Decorative gold accents */}
          <div className="absolute top-0 left-0 w-40 h-40 bg-yellow-400/10 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-yellow-400/10 rounded-full blur-2xl translate-x-1/2 translate-y-1/2"></div>

          <div className="relative z-10">
            <i className="ri-double-quotes-l text-5xl text-yellow-400/40 block mb-4"></i>
            <p className="text-base md:text-lg text-white font-medium leading-relaxed max-w-4xl mx-auto mb-6">
              With our ISO Certification and this award, we continue our mission to transform classrooms into hubs of creativity, technology, and future-ready skills — achieving{" "}
              <span className="text-yellow-400 font-bold">100% satisfaction</span>{" "}
              among students, schools, and colleges.
            </p>
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-yellow-500/20 border border-yellow-500/30 rounded-full">
              <i className="ri-building-4-line text-yellow-400"></i>
              <span className="text-yellow-300 font-semibold text-sm tracking-wide">STEMForge Tech Labs — Awarded 2025</span>
            </div>
          </div>
        </div>

        {/* Credential Badges */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { icon: "ri-trophy-fill", label: "Best Company of the Year", sub: "Aram Virudhugal, Chennai — 2025", color: "from-yellow-400 to-amber-500" },
            { icon: "ri-shield-check-fill", label: "ISO Certified", sub: "Internationally Accredited Quality", color: "from-emerald-400 to-teal-500" },
            { icon: "ri-group-fill", label: "100% Satisfaction", sub: "Students, Schools & Colleges", color: "from-teal-400 to-emerald-500" }
          ].map((badge, idx) => (
            <div key={idx} className="relative overflow-hidden rounded-2xl p-6 bg-gray-50 border border-gray-100 text-center hover:shadow-md transition-shadow">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${badge.color} flex items-center justify-center mx-auto mb-4`}>
                <i className={`${badge.icon} text-2xl text-white`}></i>
              </div>
              <div className="font-bold text-gray-900 text-base mb-1">{badge.label}</div>
              <div className="text-gray-500 text-sm">{badge.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}